import React, { useContext, useEffect, useState } from 'react';

import {
  Onboarding,
  OnboardingContext,
  OnboardingSubtitle,
  OnboardingTitle,
} from 'registration/containers/Onboarding';
import {
  TechCustomCertification,
  TechOnboardingOptions,
} from 'registration/types';

import { Checkbox } from 'shared/components/ihcl/checkbox';
import { PlusIcon } from 'shared/components/ihcl/icon';
import { Input } from 'shared/components/ihcl/input';
import { LucideIcon } from 'shared/components/ihcl/lucideIcon';
import { MatchingTextHighlight } from 'shared/components/ihcl/patterns';
import { Center } from 'shared/components/ihcl/positioning';
import { styled } from 'shared/components/ihcl/styled';
import { Text } from 'shared/components/ihcl/text';
import { Button } from 'shared/components/ihcl/v2/button';

const InputWrapper = styled('div', ({ $theme }) => ({
  marginBottom: $theme.sizing.unit24,
}));

const OnboardingTechCertifications = ({
  nextAction = null,
  onboardingWrapper = null,
  onboardingWrapperProps = {},
  previousAction = null,
  stepNumber,
}: OnboardingTechCertificationsProps) => {
  const {
    onboardingData,
    onboardingOptions = {},
    setOnboardingData,
  } = useContext(OnboardingContext);

  const { tech_certifications: unfilteredTechCertifications } =
    onboardingOptions as TechOnboardingOptions;
  const [pendingSelectedOptionsSet, setPendingSelectedOptionsSet] = useState(
    new Set<number>(onboardingData.techCertifications || [])
  );
  const [pendingCustomCertifications, setPendingCustomCertifications] =
    useState<TechCustomCertification[]>(
      onboardingData?.techCustomCertifications?.map((cert) => ({
        ...cert,
        enabled: true,
      })) || []
    );
  const [filterCertificatesValue, setFilterCertificatesValue] = useState('');
  useEffect(() => {
    setOnboardingData((data) => ({
      ...data,
      techCertifications: Array.from(pendingSelectedOptionsSet),
    }));
  }, [pendingSelectedOptionsSet, setOnboardingData]);
  useEffect(() => {
    const techCustomCertifications = (pendingCustomCertifications || [])
      .map((certification) => {
        if (certification.enabled && certification.description) {
          return {
            ...(certification.id ? { id: certification.id } : {}),
            description: certification.description,
          };
        }
        return null;
      })
      .filter(Boolean);
    setOnboardingData((data) => ({
      ...data,
      techCustomCertifications,
    }));
  }, [pendingCustomCertifications, setOnboardingData]);

  if (!onboardingData || Object.keys(onboardingData).length < 1) {
    return null;
  }

  function getTechCertifications() {
    // HACK: We need to redesign this, but in mean time request is to just show
    //       all certifications.
    return unfilteredTechCertifications;
  }

  const techCertifications = getTechCertifications();

  return (
    <Onboarding
      currentStep={stepNumber}
      isNextDisabled={
        !onboardingData?.techCertifications?.length &&
        !onboardingData?.techCustomCertifications?.length
      }
      nextAction={nextAction}
      previousAction={previousAction}
      stepKeys={['techCertifications', 'techCustomCertifications']}
      stepName="Certifications"
      onboardingWrapper={onboardingWrapper}
      onboardingWrapperProps={onboardingWrapperProps}
      validateBeforeSubmit={() => true}
    >
      <OnboardingTitle>
        What certifications and licenses do you have?
      </OnboardingTitle>
      <OnboardingSubtitle $noTopMargin>
        <Center>Please select all that apply</Center>
      </OnboardingSubtitle>
      <InputWrapper>
        <Input
          startEnhancer={<LucideIcon name="Search" size="unit16" />}
          placeholder="Look up certification or license"
          value={filterCertificatesValue}
          onChange={(e) => setFilterCertificatesValue(e.target.value)}
        />
      </InputWrapper>
      {techCertifications
        .filter(
          (filterOption) =>
            filterOption.display_title
              .toLowerCase()
              .includes(filterCertificatesValue.toLowerCase()) ||
            filterOption.internal_title
              .toLowerCase()
              .includes(filterCertificatesValue.toLowerCase())
        )
        .map(
          (option: {
            id: number;
            display_title: string;
            internal_title: string;
          }) => (
            <Checkbox
              key={option.id}
              ariaLabel={option.display_title}
              checked={
                pendingSelectedOptionsSet &&
                pendingSelectedOptionsSet.has(option.id)
              }
              onChange={(e) => {
                const newPendingSelectedOptionsSet = structuredClone(
                  pendingSelectedOptionsSet
                );
                if (e.target.checked) {
                  newPendingSelectedOptionsSet.add(option.id);
                } else {
                  newPendingSelectedOptionsSet.delete(option.id);
                }
                setPendingSelectedOptionsSet(newPendingSelectedOptionsSet);
              }}
            >
              <Text variant="ParagraphMedium">
                <MatchingTextHighlight
                  text={option.display_title}
                  searchTerm={filterCertificatesValue}
                />
              </Text>
              <Text variant="ParagraphXSmall">
                <MatchingTextHighlight
                  text={option.internal_title}
                  searchTerm={filterCertificatesValue}
                />
              </Text>
            </Checkbox>
          )
        )}

      {pendingCustomCertifications.map((certification, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <InputWrapper key={index}>
          <Checkbox
            checked={certification.enabled}
            alignItems="center"
            fullWidthLabel
            onChange={(e) => {
              const newPendingCustomCertifications = structuredClone(
                pendingCustomCertifications
              );
              newPendingCustomCertifications[index] = {
                ...certification,
                enabled: e.target.checked,
              };
              setPendingCustomCertifications(newPendingCustomCertifications);
            }}
          >
            <Input
              disabled={!certification.enabled}
              placeholder="Certification or License"
              data-testid={`customCertification${index}-description`}
              value={certification.description}
              onChange={(e) => {
                const newPendingCustomCertifications = [
                  ...pendingCustomCertifications,
                ];
                newPendingCustomCertifications[index] = {
                  ...certification,
                  description: e.target.value,
                };
                setPendingCustomCertifications(newPendingCustomCertifications);
              }}
            />
          </Checkbox>
        </InputWrapper>
      ))}

      <Button
        fullWidth
        variant="minimal"
        size="legacy-compact"
        onClick={() => {
          setPendingCustomCertifications([
            ...pendingCustomCertifications,
            { enabled: true, description: '' },
          ]);
        }}
      >
        <PlusIcon />
        Add another certification
      </Button>
    </Onboarding>
  );
};
type OnboardingTechCertificationsProps = {
  nextAction?: string;
  onboardingWrapper?: React.ComponentProps<
    typeof Onboarding
  >['onboardingWrapper'];
  onboardingWrapperProps: React.ComponentProps<
    typeof Onboarding
  >['onboardingWrapperProps'];
  previousAction?: string;
  stepNumber: number;
};

export default OnboardingTechCertifications;
